import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, ChevronLeft, Send, Wrench } from 'lucide-react';

const TroubleshootingAskAI = () => {
  const [params] = useSearchParams();
  const machineId = params.get('machine_id') || '';
  const software = params.get('software') || '';

  const [machines, setMachines] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const load = async () => {
      if (!machineId) return;
      try {
        const res = await axios.get(`${API}/machines`);
        setMachines(res.data || []);
      } catch (e) {
        console.error('Failed to load machines for troubleshooting chat', e);
      }
    };
    load();
  }, [machineId]);

  const machine = useMemo(
    () => (machineId ? machines.find((m) => m.id === machineId) || null : null),
    [machines, machineId]
  );

  const intro = useMemo(() => {
    if (machine) {
      return `Troubleshooting context loaded for machine: ${machine.name || 'Machine'}. Ask what you’re seeing (symptoms) and what operation you were doing.`;
    }
    if (software) {
      return `Troubleshooting context loaded for software/workflow: ${software}. Describe the step you’re on and what looks wrong.`;
    }
    return 'Select a machine or software from the Troubleshooting Hub to start.';
  }, [machine, software]);

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: intro,
      },
    ]);
    setSessionId(null);
  }, [intro]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const text = input;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const payload = {
        message: text,
        session_id: sessionId,
        machine_id: machineId || undefined,
        software: software || undefined,
      };

      const res = await axios.post(`${API}/ai-assistant/chat`, payload);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
    } catch (err) {
      toast.error('Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.AI_ASSISTANT)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="troubleshooting-ask-ai">
      <div className="panel-header">
        <div className="flex items-center gap-3">
          <Link to="/troubleshooting" className="btn btn-secondary btn-sm">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Link>
          <div>
            <div className="panel-title">Troubleshooting / Ask AI</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1">
              {machine ? `Machine: ${machine.name || 'Machine'}` : software ? `Software: ${software}` : 'Troubleshooting chat'}
            </h1>
          </div>
        </div>
        {(machine || software) && (
          <p className="text-sm md:text-base text-[var(--text-secondary)] mt-3 max-w-3xl">
            The AI advisor will use your selected context plus your Machine Setup profile to suggest safe,
            conservative troubleshooting steps.
          </p>
        )}
      </div>

      <div className="panel">
        <div className="flex items-center gap-2 mb-3 text-sm text-[var(--text-secondary)]">
          <Wrench className="w-4 h-4 text-amber-400" />
          <span>
            Tip: Include material, bit, RPM, feed, pass depth, and whether the issue happens on X/Y/Z moves.
          </span>
        </div>

        <ScrollArea className="h-[460px] w-full rounded-lg border border-[var(--border-subtle)] bg-[#1f1f1f] p-4">
          <div className="space-y-3">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={
                  m.role === 'user'
                    ? 'flex justify-end'
                    : 'flex justify-start'
                }
              >
                <div
                  className={
                    m.role === 'user'
                      ? 'max-w-[85%] rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/30 px-3 py-2 text-sm text-white'
                      : 'max-w-[85%] rounded-lg bg-[#2c2c2c] border border-[#3a3a3a] px-3 py-2 text-sm'
                  }
                >
                  {m.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-1 text-xs text-[#e67e22]">
                      <Bot className="w-3.5 h-3.5" />
                      AI
                    </div>
                  )}
                  <div className={`whitespace-pre-wrap leading-relaxed ${m.role === 'assistant' ? 'text-[#e67e22]' : ''}`}>{m.content}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="text-xs text-[#e67e22]">Thinking…</div>
            )}
            <div ref={endRef} />
          </div>
        </ScrollArea>

        <form onSubmit={send} className="mt-3 flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Describe the problem and what you were doing…"
            className="bg-slate-900 border-slate-700 text-white"
            disabled={isLoading || (!machineId && !software)}
          />
          <Button
            type="submit"
            disabled={isLoading || !input.trim() || (!machineId && !software)}
            className="btn btn-primary"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
        </div>
    </div>
  );
};

export default TroubleshootingAskAI;
